import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

const api = axios.create({ baseURL: API_URL });

api.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // v76: contul demo alege soferul (si vehiculul) in numele caruia face cursa GPS - backend-ul
  // ia antetele in seama DOAR pentru utilizatorul "demo" (verificat prin JWT).
  if (localStorage.getItem('username') === 'demo' && config.url && config.url.indexOf('/api/cursa-gps') !== -1) {
    const ascii = /^[\x20-\x7E]+$/;
    const sofer = localStorage.getItem('demoSofer');
    const vehicul = localStorage.getItem('demoVehicul');
    if (sofer && ascii.test(sofer)) config.headers['X-Demo-Sofer'] = sofer;
    if (vehicul && ascii.test(vehicul)) config.headers['X-Demo-Vehicul'] = vehicul;
  }
  return config;
});

function goToLogin() {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  // Folosim history.pushState in loc de window.location.href
  // ca sa nu scoata aplicatia din modul PWA standalone pe mobil
  window.history.pushState({}, '', '/login');
  window.dispatchEvent(new PopStateEvent('popstate'));
}

// Reconectare silentioasa: cand token-ul de acces a expirat (raspuns 401) dar userul
// nu a dat explicit logout, incercam automat sa obtinem un token nou folosind
// refresh-token-ul pastrat local, fara sa afisam pagina de login. Daca mai multe
// cereri esueaza simultan cu 401, doar prima declanseaza reimprospatarea - restul
// asteapta rezultatul ei (coada de mai jos), ca sa nu "ardem" refresh-token-ul de
// mai multe ori deodata (e rotit la fiecare folosire, vezi backend-ul).
let seFaceReimprospatare = false;
let coadaAsteptare = [];

function proceseazaCoada(eroare, tokenNou) {
  coadaAsteptare.forEach(({ resolve, reject, originalRequest }) => {
    if (eroare) { reject(eroare); return; }
    originalRequest.headers.Authorization = `Bearer ${tokenNou}`;
    resolve(api(originalRequest));
  });
  coadaAsteptare = [];
}

api.interceptors.response.use(
  res => res,
  async err => {
    const originalRequest = err.config;
    const esteRutaAuth = originalRequest?.url?.includes('/api/auth/login') ||
                          originalRequest?.url?.includes('/api/auth/refresh');
    // Cursa GPS are propriul mecanism de coadă locală + reîncercare silențioasă, gândit
    // special pentru semnal slab/server inaccesibil in timpul condusului - un redirect la
    // login exact atunci ar întrerupe cursa activă. Lăsăm acele cereri să-și urmeze
    // propriul flux (vezi CursaGpsTab.js), neatinse de verificarea de mai jos.
    const esteRutaCursaGps = originalRequest?.url?.includes('/api/cursa-gps/');

    // v68: un 401 (sesiune expirata) are deja reimprospatare automata mai jos. Dar daca
    // serverul e pur si simplu de neatins (restart, retea cazuta - err.response lipseste
    // complet), aplicatia ramanea blocata pe orice ecran era deschis, fara niciun semn.
    // O trimitem direct la login (de unde poate reincerca), cu exceptia Cursa GPS de mai sus.
    if (!err.response && !esteRutaAuth && !esteRutaCursaGps) {
      goToLogin();
      return Promise.reject(err);
    }

    // v74: un backend care raspunde 403 FARA mesaj (comportamentul implicit Spring Security pentru
    // o cerere fara sesiune valida - token lipsa/expirat) inseamna "nu esti autentificat", nu "n-ai
    // drepturi" - toate 403-urile reale ale aplicatiei au un camp "error" cu explicatia. Le tratam ca
    // pe un 401: reimprospatare silentioasa daca se poate, altfel direct pe pagina de login, in loc de
    // un mesaj de genul "Request failed with status code 403".
    const esteSesiuneInvalida403 = err.response?.status === 403 &&
      (!err.response.data || !err.response.data.error || err.response.data.error === 'Forbidden');
    const esteNeautentificat = err.response?.status === 401 || esteSesiuneInvalida403;

    if (esteNeautentificat && !esteRutaAuth && originalRequest && !originalRequest._retry) {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        goToLogin();
        return Promise.reject(err);
      }

      originalRequest._retry = true;

      if (seFaceReimprospatare) {
        // O alta cerere a declansat deja reimprospatarea - asteptam rezultatul ei.
        return new Promise((resolve, reject) => {
          coadaAsteptare.push({ resolve, reject, originalRequest });
        });
      }

      seFaceReimprospatare = true;
      try {
        // Cerere directa (nu prin instanta "api"), ca sa nu re-declansam interceptor-ul
        // de request (care ar atasa token-ul vechi, expirat) sau pe cel de raspuns.
        const r = await axios.post(`${API_URL}/api/auth/refresh`, { refreshToken });
        localStorage.setItem('token', r.data.token);
        localStorage.setItem('refreshToken', r.data.refreshToken);
        seFaceReimprospatare = false;
        proceseazaCoada(null, r.data.token);

        originalRequest.headers.Authorization = `Bearer ${r.data.token}`;
        return api(originalRequest);
      } catch (errReimprospatare) {
        seFaceReimprospatare = false;
        proceseazaCoada(errReimprospatare, null);
        goToLogin();
        return Promise.reject(err);
      }
    }

    return Promise.reject(err);
  }
);

export default api;
